import { Stack, Typography, Container, Grid, useTheme, useMediaQuery, Alert } from '@mui/material';
import { FC, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import usePush from 'src/hooks/use-push';
import useFetch from 'src/hooks/use-fetch';
import { CancelButton } from 'src/components/molecules/customer-cancel-modal';

interface PhotoStudioReserve {
  photo_studio_reserve_id: string;
}

export const CustomerReservationCancel: FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const cancelToken = searchParams.get('cancelToken') ?? '';
  const push = usePush();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { fetchedData: reservationStatus } = useFetch<string | PhotoStudioReserve>({
    iniState: null,
    reqProp: {
      pathKey: 'reservationCancel',
      method: 'get',
      queryParams: {
        cancelToken,
      },
    },
    disable: !cancelToken,
  });

  const handleCancelReservation = () => {
    if (!cancelToken || typeof reservationStatus === 'string') {
      if (reservationStatus === 'reservation not found' || reservationStatus === 'invalid token') {
        setError('キャンセル画面のURLが無効です。店舗へお問い合わせください。');
        return;
      }
      if (reservationStatus === 'token expired') {
        setError('キャンセル可能日時を超過しているためキャンセルできません。店舗へお問い合わせください。');
        return;
      }
      if (reservationStatus === 'reservation already cancelled') {
        setError('この予約はすでにキャンセルされています。店舗へお問い合わせください。');
        return;
      }
      return;
    }
    setIsSubmitting(true);
    setError(null);

    new Promise((resolve) =>
      push(resolve, {
        reqProp: {
          pathKey: 'reservationCancel',
          method: 'post',
          data: {
            PhotoStudioReserveId: reservationStatus.photo_studio_reserve_id,
          },
        },
        aftProp: {
          resolve,
          nextPath: `/reservation/cancel-done`,
          errorFunc: () => setIsSubmitting(false),
          showMessage: {
            state: { snackBar: { open: true, viewType: 'success', message: 'キャンセルしました' } },
          },
        },
      })
    ).then(() => {
      setIsSubmitting(false);
    });
  };

  return (
    <Container>
      <Typography variant="h4" color="text.secondary" sx={{ pb: 1, pt: 2, pl: 3 }}>
        キャンセル画面
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 3, mx: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3.5} sx={{ pb: 4 }}>
        <Grid item xs={12}>
          <Typography variant="h3" color="text.secondary" align="center" sx={{ pt: 3 }}>
            予約キャンセル画面
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography color="error" align="center" sx={{ pb: 2 }}>
            注意書き：キャンセルは予約の前日15時まで可能です。それ以降は店舗に直接お問い合わせをお願いします。
          </Typography>
          <Typography
            variant={isMdUp ? 'subtitle1' : 'subtitle2'}
            color="text.secondary"
            align="center"
            sx={{ pt: isMdUp ? 4 : 1 }}
          >
            予約をキャンセルする場合は、「予約キャンセルする」ボタンをクリックして下さい。
            <br />
            自動返信メールが届かない場合、キャンセルが確定できていない可能性がある為、店舗にご連絡ください。
            <br />
            <Typography color="error" variant="body2" align="center">
              ※証明写真プランで【お友達割引】をご利用のお客様はキャンセル不可のため、店舗へご連絡ください。
            </Typography>
            <br />
            <br />
          </Typography>
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" justifyContent="center" sx={{ m: 1, pt: 3 }} spacing={5}>
        <CancelButton onSubmit={handleCancelReservation} isSubmitting={isSubmitting} />
      </Stack>
    </Container>
  );
};
